import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  Tabs,
  Tab,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Stack,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TablePagination,
  Switch,
  FormControlLabel,
  Popover,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { es } from 'date-fns/locale';
import { format, startOfWeek, endOfWeek } from 'date-fns';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Inventory as InventoryIcon,
  ShoppingCart as ShoppingCartIcon,
  AttachMoney as AttachMoneyIcon,
  Percent as PercentIcon,
  CalendarToday as CalendarTodayIcon,
  Payments as PaymentsIcon,
  Phone as PhoneIcon,
  Place as PlaceIcon,
  Badge as BadgeIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { api } from '../services/api';
import DeleteConfirmDialog from './DeleteConfirmDialog';
// Historial de ventas eliminado del panel de administración

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: { xs: 2, sm: 3 } }}>{children}</Box>}
    </div>
  );
}

interface Product {
  id: number;
  name: string;
  description: string;
  cost_price: number;
  created_at: string;
  is_sold?: boolean;
  buyer_name?: string;
}

interface Customer {
  id: number;
  first_name: string;
  last_name: string;
  address: string;
  phone: string;
  dni: string;
  created_at: string;
  has_active_sale?: boolean;
}

interface AppUser {
  id: number;
  username: string;
  role: string;
  is_active: boolean;
}

interface Sale {
  id: number;
  customer_id: number;
  product_id: number;
  cost_price: number;
  costo_total: number;
  total_amount: number;
  installments: number;
  interest_rate: number;
  installment_amount: number;
  collection_day: number | null;
  start_date: string;
  end_date: string;
  notes?: string;
  vendor_name?: string;
  collector_name?: string;
  total_paid: number;
  is_active: boolean;
  created_at: string;
  customer: Customer;
  product: Product;
}

type DeleteDialogType = 'product' | 'customer' | 'sale' | 'expense' | '';

interface DeleteDialogState {
  open: boolean;
  type: DeleteDialogType;
  item: any;
  loading: boolean;
  error: string;
}

const createInitialDeleteDialogState = (): DeleteDialogState => ({
  open: false,
  type: '',
  item: null,
  loading: false,
  error: ''
});

const COLLECTION_DAY_LABELS = ['', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

export default function AdminPanel() {
  const [tabValue, setTabValue] = useState(0);
  const [products, setProducts] = useState<Product[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [sales, setSales] = useState<Sale[]>([]);
  const [vendors, setVendors] = useState<{ id: number; name: string }[]>([]);
  const [newVendorName, setNewVendorName] = useState('');
  const [appUsers, setAppUsers] = useState<AppUser[]>([]);
  // Buscadores
  const [productSearch, setProductSearch] = useState('');
  const [productStockFilter, setProductStockFilter] = useState<'all' | 'stock' | 'sold'>('stock');
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerStatusFilter, setCustomerStatusFilter] = useState<'active' | 'inactive' | 'all'>('active');
  const [saleSearch, setSaleSearch] = useState('');
  const [saleCollectorFilter, setSaleCollectorFilter] = useState('');
  const [saleDateFilter, setSaleDateFilter] = useState<Date | null>(null);
  const [saleRangeMode, setSaleRangeMode] = useState<'day' | 'week' | 'month'>('day');
  // Paginación
  const [productPage, setProductPage] = useState(0);
  const [customerPage, setCustomerPage] = useState(0);
  const [salePage, setSalePage] = useState(0);
  const rowsPerPage = 25;
  // Gastos
  const [selectedExpenseDate, setSelectedExpenseDate] = useState<Date>(new Date());
  const [expenseViewMode, setExpenseViewMode] = useState<'daily' | 'weekly'>('daily');
  const [selectedExpenseWeek, setSelectedExpenseWeek] = useState<Date>(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const jsDay = today.getDay();
    const offsetToWednesday = (jsDay + 4) % 7;
    today.setDate(today.getDate() - offsetToWednesday);
    return today;
  });
  const [weeklyExpenses, setWeeklyExpenses] = useState<any[]>([]);
  const [weeklyExpensesByResponsible, setWeeklyExpensesByResponsible] = useState<any[]>([]);
  const [expenses, setExpenses] = useState<any[]>([]);
  const [expenseDialog, setExpenseDialog] = useState({ open: false, mode: 'create' as 'create' | 'edit', expense: null as any });
  const [expenseForm, setExpenseForm] = useState({ description: '', total: '', cash_amount: '', transfer_amount: '', category: '', responsible: '', is_product: false, expense_date: new Date() as Date });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Estados para diálogos
  const [productDialog, setProductDialog] = useState({ open: false, mode: 'create' as 'create' | 'edit', product: null as any });
  const [customerDialog, setCustomerDialog] = useState({ open: false, mode: 'create' as 'create' | 'edit', customer: null as any });
  const [saleDialog, setSaleDialog] = useState({ open: false, mode: 'create' as 'create' | 'edit', sale: null as any });
  
  // Estado para diálogo de confirmación de eliminación
  const [deleteDialog, setDeleteDialog] = useState<DeleteDialogState>(createInitialDeleteDialogState());

  // Estados para formularios
  const [productForm, setProductForm] = useState({ name: '', description: '', cost_price: 0, buyer_name: '' });
  const [customerForm, setCustomerForm] = useState({ 
    first_name: '', last_name: '', address: '', phone: '', dni: '' 
  });
  const [saleForm, setSaleForm] = useState({
    customer_id: 0, product_id: 0, cost_price: 0, costo_total: 0, total_amount: 0, installments: 0,
    interest_rate: 0, installment_amount: 0, collection_day: 0 as number, start_date: '', end_date: '', notes: '', vendor_name: '', collector_name: ''
  });
  const [payFirstInstallment, setPayFirstInstallment] = useState(false);
  const [firstInstallmentMethod, setFirstInstallmentMethod] = useState<'cash' | 'transfer'>('cash');
  // En edición: si la venta ya tiene la primera cuota cubierta, no se puede volver a cobrar
  const [firstInstallmentAlreadyPaid, setFirstInstallmentAlreadyPaid] = useState(false);

  const selectedCustomer = useMemo(
    () => customers.find((customer) => customer.id === saleForm.customer_id) || null,
    [customers, saleForm.customer_id]
  );

  const [saleItems, setSaleItems] = useState<{ product: Product; quantity: number }[]>([]);
  const [productSelection, setProductSelection] = useState<{ product: Product | null; quantity: number }>({
    product: null,
    quantity: 1,
  });

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0,
    }).format(Number.isFinite(value) ? value : 0);

  const parseISODate = (value?: string | null) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return null;
    }
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  };

  const estimatedSchedule = useMemo(() => {
    const explicitStart = parseISODate(saleForm.start_date);
    const explicitEnd = parseISODate(saleForm.end_date);

    if (explicitStart && explicitEnd) {
      return { start: explicitStart, end: explicitEnd, isEstimated: false };
    }

    const installments = Number.isFinite(saleForm.installments) && saleForm.installments > 0
      ? saleForm.installments
      : 0;
    const collectionDay = Number.isFinite(saleForm.collection_day) && saleForm.collection_day >= 1 && saleForm.collection_day <= 7
      ? saleForm.collection_day
      : null;

    if (!installments) {
      return { start: null, end: null, isEstimated: true };
    }

    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    // Cuota 1 = hoy (día de venta). Cuota 2+ alineada a collectionDay.
    let end: Date;
    if (installments <= 1) {
      end = new Date(start);
    } else {
      // Base para cuota 2: start + 7 días, alineado al collectionDay
      const base = new Date(start);
      base.setDate(base.getDate() + 7);
      if (collectionDay) {
        const jsDay = base.getDay();
        const baseDay = jsDay === 0 ? 7 : jsDay;
        const diff = (collectionDay - baseDay + 7) % 7;
        base.setDate(base.getDate() + diff);
      }
      // Cuota N = base + (N-2) * 7
      end = new Date(base);
      end.setDate(base.getDate() + (installments - 2) * 7);
    }

    return { start, end, isEstimated: true };
  }, [saleForm.collection_day, saleForm.installments, saleForm.start_date, saleForm.end_date]);

  const estimatedStartLabel = estimatedSchedule.start
    ? estimatedSchedule.start.toLocaleDateString('es-AR')
    : 'Define cuotas';

  const estimatedEndLabel = estimatedSchedule.end
    ? estimatedSchedule.end.toLocaleDateString('es-AR')
    : 'Define cuotas';
  const formattedInterestRate = Number.isFinite(saleForm.interest_rate) ? saleForm.interest_rate : 0;
  const collectionDayLabel = saleForm.collection_day ? (COLLECTION_DAY_LABELS[saleForm.collection_day] || 'Sin día fijo') : 'Sin día fijo';
  const isSaleFormValid = Boolean(
    saleForm.customer_id &&
    (saleForm.product_id || saleItems.length > 0) &&
    saleForm.cost_price > 0 &&
    saleForm.total_amount > 0 &&
    saleForm.installments > 0 &&
    saleForm.installment_amount > 0
  );

  const filteredProducts = useMemo(() => {
    let filtered = products;
    if (productStockFilter === 'stock') filtered = filtered.filter(p => !p.is_sold);
    else if (productStockFilter === 'sold') filtered = filtered.filter(p => p.is_sold);
    if (productSearch.trim()) {
      const term = productSearch.toLowerCase();
      filtered = filtered.filter(p =>
        p.name.toLowerCase().includes(term) ||
        (p.description || '').toLowerCase().includes(term)
      );
    }
    return filtered;
  }, [products, productSearch, productStockFilter]);

  const filteredCustomers = useMemo(() => {
    let list = customers;
    if (customerStatusFilter === 'active') {
      list = list.filter(c => c.has_active_sale);
    } else if (customerStatusFilter === 'inactive') {
      list = list.filter(c => !c.has_active_sale);
    }
    if (customerSearch.trim()) {
      const term = customerSearch.toLowerCase();
      list = list.filter(c =>
        c.first_name.toLowerCase().includes(term) ||
        c.last_name.toLowerCase().includes(term) ||
        (c.dni || '').toLowerCase().includes(term) ||
        (c.phone || '').toLowerCase().includes(term)
      );
    }
    return list;
  }, [customers, customerSearch, customerStatusFilter]);

  const filteredSales = useMemo(() => {
    let result = sales;
    if (saleSearch.trim()) {
      const term = saleSearch.toLowerCase();
      result = result.filter(s =>
        `${s.customer.last_name} ${s.customer.first_name}`.toLowerCase().includes(term) ||
        s.product.name.toLowerCase().includes(term) ||
        (s.vendor_name || '').toLowerCase().includes(term) ||
        (s.collector_name || '').toLowerCase().includes(term)
      );
    }
    if (saleCollectorFilter) {
      result = result.filter(s => s.collector_name === saleCollectorFilter);
    }
    if (saleDateFilter) {
      result = result.filter(s => {
        if (!s.created_at) return false;
        const d = new Date(s.created_at);
        if (saleRangeMode === 'day') {
          return d.getFullYear() === saleDateFilter.getFullYear() &&
            d.getMonth() === saleDateFilter.getMonth() &&
            d.getDate() === saleDateFilter.getDate();
        }
        if (saleRangeMode === 'month') {
          return d.getFullYear() === saleDateFilter.getFullYear() &&
            d.getMonth() === saleDateFilter.getMonth();
        }
        const weekStart = startOfWeek(saleDateFilter, { weekStartsOn: 1 });
        const weekEnd = endOfWeek(saleDateFilter, { weekStartsOn: 1 });
        return d >= weekStart && d <= weekEnd;
      });
    }
    return result;
  }, [sales, saleSearch, saleCollectorFilter, saleDateFilter, saleRangeMode]);

  const resolveCostPrice = () => {
    if (saleForm.cost_price) {
      return saleForm.cost_price;
    }
    if (saleItems.length > 0) {
      return saleItems.reduce((sum, item) => sum + (item.product.cost_price || 0) * item.quantity, 0);
    }
    const single = products.find((p) => p.id === saleForm.product_id);
    return single?.cost_price ?? 0;
  };

  // Per-tab fetch functions
  const fetchProducts = useCallback(async () => {
    try {
      const res = await api.get('/products/stock-status');
      setProducts(res.data);
    } catch { /* silently fail, error shown by caller if needed */ }
  }, []);

  const fetchCustomers = useCallback(async () => {
    try {
      // Pedimos todos y filtramos local; el flag has_active_sale viene en cada item
      const res = await api.get('/customers?status=all');
      setCustomers(res.data);
    } catch { /* silently fail */ }
  }, []);

  const fetchVendors = useCallback(async () => {
    try {
      const res = await api.get('/vendors');
      setVendors(res.data || []);
    } catch { /* silently fail */ }
  }, []);

  const fetchSalesData = useCallback(async () => {
    try {
      const [salesRes, vendorsRes, usersRes] = await Promise.all([
        api.get('/sales'),
        api.get('/vendors'),
        api.get('/users').catch(() => ({ data: [] }))
      ]);
      setSales(salesRes.data);
      setVendors(vendorsRes.data || []);
      setAppUsers(usersRes.data || []);
    } catch { /* silently fail */ }
  }, []);

  const loadedTabs = useRef<Set<number>>(new Set());

  const fetchTabData = useCallback(async (tab: number) => {
    if (loadedTabs.current.has(tab)) return;
    setLoading(true);
    try {
      switch (tab) {
        case 0:
          await Promise.all([fetchProducts(), fetchVendors()]);
          break;
        case 1:
          await fetchCustomers();
          break;
        case 2:
          // Sales tab needs customers & products for the form too
          await Promise.all([
            fetchSalesData(),
            ...(loadedTabs.current.has(0) ? [] : [fetchProducts()]),
            ...(loadedTabs.current.has(1) ? [] : [fetchCustomers()]),
          ]);
          loadedTabs.current.add(0);
          loadedTabs.current.add(1);
          break;
        case 3:
          // Expenses tab loads via fetchDailyFinance
          break;
        case 4:
          // Users tab - fetch users list
          await api.get('/users').then(res => setAppUsers(res.data || [])).catch(() => {});
          break;
      }
      loadedTabs.current.add(tab);
    } catch {
      setError('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  }, [fetchProducts, fetchCustomers, fetchSalesData]);

  // Load initial tab on mount
  useEffect(() => {
    fetchTabData(tabValue);
  }, []);

  // Load data when switching tabs
  useEffect(() => {
    fetchTabData(tabValue);
    if (tabValue === 3) {
      if (expenseViewMode === 'daily') {
        fetchDailyFinance(selectedExpenseDate);
      } else {
        fetchWeeklyExpenses(selectedExpenseWeek);
      }
    }
  }, [tabValue, selectedExpenseDate, expenseViewMode, selectedExpenseWeek]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const formatYmd = (d: Date) => {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const fetchWeeklyExpenses = async (weekStart: Date) => {
    try {
      const dateStr = formatYmd(weekStart);
      const res = await api.get('/finance/weekly', { params: { week: dateStr } });
      const days = res.data?.days || [];
      setWeeklyExpenses(days);
      setWeeklyExpensesByResponsible(res.data?.expenses_by_responsible || []);
    } catch (e) {
      setWeeklyExpenses([]);
      setWeeklyExpensesByResponsible([]);
    }
  };

  const fetchDailyFinance = async (date: Date) => {
    try {
      const dateStr = formatYmd(date);
      const res = await api.get('/finance/daily', { params: { date: dateStr } });
      const ops = res.data?.operations || [];
      const dailyExpenses = ops.filter((op: any) => op.type === 'expense');
      setExpenses(dailyExpenses);
    } catch (e) {
      setExpenses([]);
    }
  };

  const handleCreateExpense = async () => {
    try {
      const cash = parseFloat((expenseForm.cash_amount || '0') as any) || 0;
      const transfer = parseFloat((expenseForm.transfer_amount || '0') as any) || 0;
      const normalizedTotal = cash + transfer;
      const expenseDate = expenseForm.expense_date || new Date();

      if (expenseForm.is_product) {
        // El backend crea el gasto automáticamente al crear el producto
        await api.post('/products', {
          name: expenseForm.description,
          cost_price: normalizedTotal,
          buyer_name: expenseForm.responsible,
          expense_category: expenseForm.category || 'productos',
          cash_amount: cash,
          transfer_amount: transfer,
          expense_date: formatYmd(expenseDate),
        });
        fetchProducts();
      } else {
        const payload: any = {
          description: expenseForm.description,
          amount: normalizedTotal,
          category: expenseForm.category || 'general',
          expense_date: formatYmd(expenseDate),
          notes: JSON.stringify({ cash_amount: cash, transfer_amount: transfer, responsible: expenseForm.responsible })
        };
        await api.post('/expenses', payload);
      }

      setExpenseForm({ description: '', total: '', cash_amount: '', transfer_amount: '', category: '', responsible: '', is_product: false, expense_date: new Date() });
      setExpenseDialog({ open: false, mode: 'create', expense: null });
      // Mostrar el día del gasto recién cargado para que quede a la vista
      setExpenseViewMode('daily');
      setSelectedExpenseDate(expenseDate);
      fetchDailyFinance(expenseDate);
      setSuccess('Gasto creado');
    } catch (e) {
      setError('Error al crear el gasto');
    }
  };

  const handleUpdateExpense = async () => {
    if (!expenseDialog.expense?.expense_id) return;
    try {
      const cash = parseFloat((expenseForm.cash_amount || '0') as any) || 0;
      const transfer = parseFloat((expenseForm.transfer_amount || '0') as any) || 0;
      const normalizedTotal = cash + transfer;

      const expenseDate = expenseForm.expense_date || new Date();
      const payload: any = {
        description: expenseForm.description,
        amount: normalizedTotal,
        category: expenseForm.category || 'general',
        expense_date: formatYmd(expenseDate),
        notes: JSON.stringify({ cash_amount: cash, transfer_amount: transfer, responsible: expenseForm.responsible })
      };
      await api.put(`/expenses/${expenseDialog.expense.expense_id}`, payload);
      setExpenseForm({ description: '', total: '', cash_amount: '', transfer_amount: '', category: '', responsible: '', is_product: false, expense_date: new Date() });
      setExpenseDialog({ open: false, mode: 'create', expense: null });
      setSelectedExpenseDate(expenseDate);
      fetchDailyFinance(expenseDate);
      setSuccess('Gasto actualizado');
    } catch (e) {
      setError('Error al actualizar el gasto');
    }
  };

  const handleCreateProduct = async () => {
    try {
      await api.post('/products', productForm);
      setProductDialog({ open: false, mode: 'create', product: null });
      setProductForm({ name: '', description: '', cost_price: 0, buyer_name: '' });
      fetchProducts();
      setSuccess('Producto creado exitosamente');
    } catch (error) {
      setError('Error al crear el producto');
    }
  };

  const handleCreateCustomer = async () => {
    try {
      if (!customerForm.dni.trim()) {
        setError('Ingresa un DNI para el cliente');
        return;
      }

      await api.post('/customers', customerForm);
      setCustomerDialog({ open: false, mode: 'create', customer: null });
      setCustomerForm({ first_name: '', last_name: '', address: '', phone: '', dni: '' });
      fetchCustomers();
      setSuccess('Cliente creado exitosamente');
    } catch (error) {
      setError('Error al crear el cliente');
    }
  };

  const handleCreateSale = async () => {
    try {
      if (!saleForm.customer_id) {
        setError('Selecciona un cliente');
        return;
      }
      if (!saleForm.product_id && saleItems.length === 0) {
        setError('Selecciona al menos un producto');
        return;
      }

      const costPrice = resolveCostPrice();
      if (!costPrice || costPrice <= 0) {
        setError('Ingresa un precio de costo válido');
        return;
      }

      const installments = Number.isFinite(saleForm.installments) ? saleForm.installments : 0;
      if (!installments || installments <= 0) {
        setError('Ingresa un número de cuotas válido');
        return;
      }

      const installmentAmount = Number.isFinite(saleForm.installment_amount) && saleForm.installment_amount > 0
        ? saleForm.installment_amount : 0;

      if (!installmentAmount || installmentAmount <= 0) {
        setError('Ingresa un monto por cuota válido');
        return;
      }

      const normalizedTotal = installmentAmount * installments;
      const interestRate = calculateInterestRate(costPrice, installments, normalizedTotal);

      const productNames = saleItems.length > 0
        ? saleItems.map((item) =>
            `${item.product.name}${item.quantity > 1 ? ` x${item.quantity}` : ''}`
          )
        : [];
      const payload = {
        ...saleForm,
        cost_price: costPrice,
        installments,
        total_amount: normalizedTotal,
        installment_amount: installmentAmount,
        interest_rate: interestRate,
        costo_total: normalizedTotal,
        collection_day: saleForm.collection_day || null,
        product_id: saleItems[0]?.product.id || saleForm.product_id,
        product_ids: saleItems.length > 0 ? saleItems.map((item) => item.product.id) : undefined,
        notes:
          productNames.length > 1 || saleItems.some((item) => item.quantity > 1)
            ? `Productos: ${productNames.join(' + ')}`
            : saleForm.notes,
        vendor_name: saleForm.vendor_name || undefined,
        collector_name: saleForm.collector_name || undefined
      };

      const saleRes = await api.post('/sales', sanitizeSalePayload(payload));

      // Si se marcó pagar primera cuota, crear el pago
      if (payFirstInstallment && saleRes.data?.id) {
        const now = new Date(); const today = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
        try {
          await api.post('/payments', {
            sale_id: saleRes.data.id,
            installment_number: 1,
            amount: installmentAmount,
            payment_method: firstInstallmentMethod,
            cash_amount: firstInstallmentMethod === 'cash' ? installmentAmount : 0,
            transfer_amount: firstInstallmentMethod === 'transfer' ? installmentAmount : 0,
            payment_date: today,
            notes: 'Pago primera cuota al momento de la venta',
          });
        } catch {
          // Sale was created but payment failed - still show success
          setError('Venta creada pero no se pudo registrar el pago de la primera cuota');
        }
      }

      setSaleDialog({ open: false, mode: 'create', sale: null });
      setSaleForm({
        customer_id: 0, product_id: 0, cost_price: 0, costo_total: 0, total_amount: 0, installments: 0,
        interest_rate: 0, installment_amount: 0, collection_day: 0 as number, start_date: '', end_date: '', notes: '', vendor_name: '', collector_name: ''
      });
      setPayFirstInstallment(false);
      setSaleItems([]);
      resetProductSelection();
      fetchSalesData();
      fetchProducts(); // refresh is_sold status
      setSuccess('Venta creada exitosamente');
    } catch (error) {
      setError('Error al crear la venta');
    }
  };

  // Funciones para abrir diálogos de edición
  const handleEditProduct = (product: any) => {
    setProductForm({
      name: product.name,
      description: product.description || '',
      cost_price: product.cost_price,
      buyer_name: product.buyer_name || '',
    });
    setProductDialog({ open: true, mode: 'edit', product });
  };

  const handleEditCustomer = (customer: any) => {
    setCustomerForm({
      first_name: customer.first_name,
      last_name: customer.last_name,
      address: customer.address,
      phone: customer.phone || '',
      dni: customer.dni || ''
    });
    setCustomerDialog({ open: true, mode: 'edit', customer });
  };

  const handleEditSale = (sale: any) => {
    setSaleForm({
      customer_id: sale.customer_id,
      product_id: sale.product_id,
      cost_price: sale.cost_price || (sale.product ? sale.product.cost_price : 0),
      costo_total: sale.costo_total || 0,
      total_amount: sale.total_amount,
      installments: sale.installments,
      interest_rate: sale.interest_rate,
      installment_amount: sale.installment_amount,
      collection_day: sale.collection_day,
      start_date: sale.start_date,
      end_date: sale.end_date,
      notes: sale.notes || '',
      vendor_name: sale.vendor_name || '',
      collector_name: sale.collector_name || ''
    });
    setSaleItems([]);
    resetProductSelection();
    const alreadyPaid =
      Number(sale.total_paid || 0) >= Number(sale.installment_amount || 0) - 0.01 &&
      Number(sale.installment_amount || 0) > 0;
    setFirstInstallmentAlreadyPaid(alreadyPaid);
    setPayFirstInstallment(alreadyPaid);
    setFirstInstallmentMethod('cash');
    setSaleDialog({ open: true, mode: 'edit', sale });
  };

  // Funciones para actualizar elementos
  const handleUpdateProduct = async () => {
    try {
      await api.put(`/products/${productDialog.product.id}`, productForm);
      setProductDialog({ open: false, mode: 'create', product: null });
      setProductForm({ name: '', description: '', cost_price: 0, buyer_name: '' });
      fetchProducts();
      setSuccess('Producto actualizado exitosamente');
    } catch (error) {
      setError('Error al actualizar el producto');
    }
  };

  const handleUpdateCustomer = async () => {
    try {
      if (!customerForm.dni.trim()) {
        setError('Ingresa un DNI para el cliente');
        return;
      }

      await api.put(`/customers/${customerDialog.customer.id}`, customerForm);
      setCustomerDialog({ open: false, mode: 'create', customer: null });
      setCustomerForm({ first_name: '', last_name: '', address: '', phone: '', dni: '' });
      fetchCustomers();
      setSuccess('Cliente actualizado exitosamente');
    } catch (error) {
      setError('Error al actualizar el cliente');
    }
  };

  const handleUpdateSale = async () => {
    if (!saleDialog.sale) {
      return;
    }

    try {
      const costPrice = resolveCostPrice();
      const installments = Number.isFinite(saleForm.installments) ? saleForm.installments : 0;
      const installmentAmount = Number.isFinite(saleForm.installment_amount) ? saleForm.installment_amount : 0;
      const normalizedTotal = installments > 0 && installmentAmount > 0
        ? installments * installmentAmount : saleForm.total_amount || 0;
      const interestRate = calculateInterestRate(costPrice, installments, normalizedTotal);

      const productNames = saleItems.length > 0
        ? saleItems.map((item) => `${item.product.name}${item.quantity > 1 ? ` x${item.quantity}` : ''}`)
        : [];

      const payload = {
        ...saleForm,
        cost_price: costPrice,
        installments,
        total_amount: normalizedTotal,
        installment_amount: installmentAmount,
        interest_rate: interestRate,
        costo_total: normalizedTotal,
        collection_day: saleForm.collection_day || null,
        product_id: saleItems[0]?.product.id || saleForm.product_id,
        product_ids: saleItems.length > 0 ? saleItems.map((item) => item.product.id) : undefined,
        notes:
          saleItems.length > 0 && (productNames.length > 1 || saleItems.some((item) => item.quantity > 1))
            ? `Productos: ${productNames.join(' + ')}`
            : saleForm.notes,
      };

      await api.put(`/sales/${saleDialog.sale.id}`, sanitizeSalePayload(payload));

      // Si al editar se marca que el cliente pagó la primera cuota y todavía no
      // estaba registrada, crear el pago para que aparezca en cobranzas.
      if (payFirstInstallment && !firstInstallmentAlreadyPaid && installmentAmount > 0) {
        const now = new Date();
        const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
        try {
          await api.post('/payments', {
            sale_id: saleDialog.sale.id,
            installment_number: 1,
            amount: installmentAmount,
            payment_method: firstInstallmentMethod,
            cash_amount: firstInstallmentMethod === 'cash' ? installmentAmount : 0,
            transfer_amount: firstInstallmentMethod === 'transfer' ? installmentAmount : 0,
            payment_date: today,
            notes: 'Pago primera cuota registrado al editar la venta',
          });
        } catch {
          setError('Venta actualizada pero no se pudo registrar el pago de la primera cuota');
        }
      }

      setSaleDialog({ open: false, mode: 'create', sale: null });
      setSaleForm({
        customer_id: 0, product_id: 0, cost_price: 0, costo_total: 0, total_amount: 0, installments: 0,
        interest_rate: 0, installment_amount: 0, collection_day: 0 as number, start_date: '', end_date: '', notes: '', vendor_name: '', collector_name: ''
      });
      setPayFirstInstallment(false);
      setFirstInstallmentAlreadyPaid(false);
      setSaleItems([]);
      resetProductSelection();
      fetchSalesData();
      fetchProducts(); // refresh is_sold status
      setSuccess('Venta actualizada exitosamente');
    } catch (error) {
      setError('Error al actualizar la venta');
    }
  };

  // Funciones para cálculos dinámicos

  const calculateInterestRate = (costPrice: number, installments: number, totalAmount: number) => {
    const sanitizedCost = Number.isFinite(costPrice) && costPrice > 0 ? costPrice : 0;
    const sanitizedTotal = Number.isFinite(totalAmount) && totalAmount > 0 ? totalAmount : 0;

    if (!sanitizedCost || !sanitizedTotal) {
      return 0;
    }

    const effectiveRate = sanitizedTotal / sanitizedCost - 1;
    const ratePercentage = effectiveRate * 100;
    return Number.isFinite(ratePercentage) ? Math.max(ratePercentage, 0) : 0;
  };

  const sanitizeSalePayload = (payload: any) => {
    const cleanPayload = { ...payload };
    if (!cleanPayload.start_date) {
      delete cleanPayload.start_date;
    }
    if (!cleanPayload.end_date) {
      delete cleanPayload.end_date;
    }
    return cleanPayload;
  };

  const calculateSaleAmounts = (costPrice: number, installments: number, interestRate: number) => {
    const sanitizedCost = Number.isFinite(costPrice) && costPrice > 0 ? costPrice : 0;
    const normalizedInstallments = Number.isFinite(installments) && installments > 0 ? installments : 0;
    const sanitizedInterest = Number.isFinite(interestRate) && interestRate >= 0 ? interestRate : 0;

    if (!sanitizedCost) {
      return { totalAmount: 0, installmentAmount: 0 };
    }

    const totalCharged = sanitizedCost * (1 + sanitizedInterest / 100);
    const installmentAmount = normalizedInstallments > 0 ? totalCharged / normalizedInstallments : 0;

    return {
      totalAmount: Number.isFinite(totalCharged) ? totalCharged : 0,
      installmentAmount: Number.isFinite(installmentAmount) ? installmentAmount : 0,
    };
  };

  const computeSaleItemsCost = (items: { product: Product; quantity: number }[]) => {
    return items.reduce(
      (sum, item) => sum + (item.product.cost_price || 0) * Math.max(item.quantity, 0),
      0
    );
  };

  const applySaleItemsUpdate = (items: { product: Product; quantity: number }[]) => {
    const normalizedItems = items.filter((item) => item.quantity > 0);
    const totalCost = computeSaleItemsCost(normalizedItems);

    setSaleItems(normalizedItems);
    setSaleForm((prev) => ({
      ...prev,
      cost_price: totalCost,
      product_id: normalizedItems[0]?.product.id || 0,
    }));
  };

  const resetProductSelection = () => {
    setProductSelection({ product: null, quantity: 1 });
  };

  const handleAddSaleItem = () => {
    const selectedProduct = productSelection.product;
    const rawQuantity = Number(productSelection.quantity);

    if (!selectedProduct) {
      setError('Selecciona un producto para agregar a la venta');
      return;
    }

    const desiredQuantity = Number.isFinite(rawQuantity) && rawQuantity > 0 ? Math.max(1, Math.floor(rawQuantity)) : 1;
    const existingItem = saleItems.find((item) => item.product.id === selectedProduct.id);

    const updatedItems = existingItem
      ? saleItems.map((item) =>
          item.product.id === selectedProduct.id
            ? { ...item, quantity: item.quantity + desiredQuantity }
            : item
        )
      : [...saleItems, { product: selectedProduct, quantity: desiredQuantity }];

    applySaleItemsUpdate(updatedItems);
    setError((prev) =>
      prev === 'Selecciona un producto para agregar a la venta' ? '' : prev
    );
    resetProductSelection();
  };

  const handleSaleItemQuantityChange = (productId: number, rawQuantity: number) => {
    const targetItem = saleItems.find((item) => item.product.id === productId);
    if (!targetItem) {
      return;
    }

    const normalized = Number.isFinite(rawQuantity) && rawQuantity > 0 ? Math.floor(rawQuantity) : 1;
    const sanitizedQuantity = Math.max(1, normalized);

    const updatedItems = saleItems.map((item) =>
      item.product.id === productId ? { ...item, quantity: sanitizedQuantity } : item
    );

    applySaleItemsUpdate(updatedItems);
  };

  const handleRemoveSaleItem = (productId: number) => {
    const updatedItems = saleItems.filter((item) => item.product.id !== productId);
    applySaleItemsUpdate(updatedItems);
  };

  // Funciones de eliminación
  const openDeleteDialogWithItem = (type: DeleteDialogType, item: any) => {
    setDeleteDialog({
      ...createInitialDeleteDialogState(),
      open: true,
      type,
      item
    });
  };

  const handleDeleteProduct = (product: Product) => {
    openDeleteDialogWithItem('product', product);
  };

  const handleDeleteCustomer = (customer: Customer) => {
    openDeleteDialogWithItem('customer', customer);
  };

  const handleDeleteSale = (sale: Sale) => {
    openDeleteDialogWithItem('sale', sale);
  };

  const handleDeleteExpense = (expense: any) => {
    openDeleteDialogWithItem('expense', expense);
  };

  const getDeleteEntityLabel = (type: DeleteDialogType) => {
    switch (type) {
      case 'product':
        return 'Producto';
      case 'customer':
        return 'Cliente';
      case 'sale':
        return 'Venta';
      case 'expense':
        return 'Gasto';
      default:
        return '';
    }
  };

  const confirmDelete = async () => {
    setDeleteDialog(prev => ({ ...prev, loading: true, error: '' }));

    try {
      const { type, item } = deleteDialog;
      let endpoint = '';
      let refreshFn: (() => Promise<void>) | null = null;

      switch (type) {
        case 'product':
          endpoint = `/products/${item.id}`;
          refreshFn = fetchProducts;
          break;
        case 'customer':
          endpoint = `/customers/${item.id}`;
          refreshFn = fetchCustomers;
          break;
        case 'sale':
          endpoint = `/sales/${item.id}`;
          refreshFn = async () => { await fetchSalesData(); await fetchProducts(); };
          break;
        case 'expense':
          if (!item?.expense_id) {
            throw new Error('Identificador de gasto inválido');
          }
          endpoint = `/expenses/${item.expense_id}`;
          refreshFn = () => fetchDailyFinance(selectedExpenseDate);
          break;
        default:
          throw new Error('Tipo de elemento no válido');
      }

      await api.delete(endpoint);
      setDeleteDialog(createInitialDeleteDialogState());

      if (refreshFn) {
        await refreshFn();
      }

      const entityLabel = getDeleteEntityLabel(type) || 'Elemento';
      setSuccess(`${entityLabel} eliminado exitosamente`);
    } catch (error: any) {
      const entityLabel = getDeleteEntityLabel(deleteDialog.type);
      const rawDetail = error.response?.data?.detail;
      let formatted: string;
      if (rawDetail && typeof rawDetail === 'object') {
        const msg = rawDetail.message || 'No se puede eliminar';
        const related = Array.isArray(rawDetail.related_sales) ? rawDetail.related_sales : [];
        if (related.length > 0) {
          const items = related.slice(0, 5).map((s: any) =>
            `• Venta #${s.sale_id} — ${s.customer_name}${s.start_date ? ` (${s.start_date})` : ''}${s.total_amount ? ` — $${Number(s.total_amount).toLocaleString()}` : ''}`
          ).join('\n');
          const moreNote = related.length > 5 ? `\n…y ${related.length - 5} más` : '';
          formatted = `${msg}:\n${items}${moreNote}`;
        } else {
          formatted = msg;
        }
      } else {
        formatted = rawDetail || `Error al eliminar el ${entityLabel ? entityLabel.toLowerCase() : 'elemento'}`;
      }
      setDeleteDialog(prev => ({
        ...prev,
        loading: false,
        error: formatted,
      }));
    }
  };

  const getDeleteDialogItemName = () => {
    switch (deleteDialog.type) {
      case 'product':
        return deleteDialog.item?.name || '';
      case 'customer': {
        const firstName = deleteDialog.item?.first_name ?? '';
        const lastName = deleteDialog.item?.last_name ?? '';
        return `${firstName} ${lastName}`.trim();
      }
      case 'sale':
        return deleteDialog.item ? `Venta #${deleteDialog.item.id}` : '';
      case 'expense': {
        const description = deleteDialog.item?.description || '';
        const rawAmount = deleteDialog.item?.expense ?? deleteDialog.item?.amount;
        if (rawAmount !== undefined && rawAmount !== null) {
          const amount = Number(rawAmount);
          if (!Number.isNaN(amount) && amount > 0) {
            const formattedAmount = formatCurrency(amount);
            return description ? `${description} - ${formattedAmount}` : formattedAmount;
          }
        }
        return description;
      }
      default:
        return '';
    }
  };

  const deleteEntityLabel = getDeleteEntityLabel(deleteDialog.type);
  const deleteDialogItemLabel = getDeleteDialogItemName();
  const deleteDialogMessageSubject = deleteEntityLabel ? deleteEntityLabel.toLowerCase() : 'elemento';

  const handleProductChange = (productId: number) => {
    const selectedProduct = products.find(p => p.id === productId);
    if (selectedProduct) {
      setSaleForm({ ...saleForm, product_id: productId, cost_price: selectedProduct.cost_price });
    } else {
      setSaleForm({ ...saleForm, product_id: productId, cost_price: 0 });
    }
  };

  const handleInstallmentsChange = (installments: number) => {
    const normalizedInstallments = Number.isFinite(installments) && installments > 0 ? installments : 0;
    const installmentAmount = Number.isFinite(saleForm.installment_amount) ? saleForm.installment_amount : 0;
    const totalAmount = normalizedInstallments > 0 && installmentAmount > 0
      ? normalizedInstallments * installmentAmount : 0;
    const costPrice = resolveCostPrice();
    const interestRate = totalAmount > 0 && costPrice > 0
      ? calculateInterestRate(costPrice, normalizedInstallments, totalAmount) : 0;

    setSaleForm({
      ...saleForm,
      installments: normalizedInstallments,
      total_amount: totalAmount,
      interest_rate: interestRate,
      costo_total: totalAmount,
    });
  };

  const handleInstallmentAmountChange = (installmentAmount: number) => {
    const sanitizedAmount = Number.isFinite(installmentAmount) && installmentAmount > 0 ? installmentAmount : 0;
    const installments = saleForm.installments;
    const totalAmount = installments > 0 && sanitizedAmount > 0
      ? installments * sanitizedAmount : 0;
    const costPrice = resolveCostPrice();
    const interestRate = totalAmount > 0 && costPrice > 0
      ? calculateInterestRate(costPrice, installments, totalAmount) : 0;

    setSaleForm({
      ...saleForm,
      installment_amount: sanitizedAmount,
      total_amount: totalAmount,
      interest_rate: interestRate,
      costo_total: totalAmount,
    });
  };

  const handleCostPriceChange = (costPrice: number) => {
    const sanitizedCost = Number.isFinite(costPrice) && costPrice > 0 ? costPrice : 0;
    const totalAmount = saleForm.total_amount;
    const installments = saleForm.installments;
    const interestRate = totalAmount > 0 && sanitizedCost > 0
      ? calculateInterestRate(sanitizedCost, installments, totalAmount) : 0;

    setSaleForm({
      ...saleForm,
      cost_price: sanitizedCost,
      interest_rate: interestRate,
    });
  };

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, sm: 4 }, mb: { xs: 2, sm: 4 } }}>
      <Typography 
        variant="h4" 
        gutterBottom
        sx={{ 
          fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' },
          textAlign: { xs: 'center', sm: 'left' }
        }}
      >
        Panel de Administración
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Paper sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': {
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                minHeight: { xs: 48, sm: 64 },
                px: { xs: 1, sm: 2 }
              }
            }}
          >
            <Tab icon={<InventoryIcon />} label="Productos" />
            <Tab icon={<PersonIcon />} label="Clientes" />
            <Tab icon={<ShoppingCartIcon />} label="Ventas" />
            <Tab icon={<AttachMoneyIcon />} label="Gastos" />
            <Tab icon={<BadgeIcon />} label="Usuarios" />
          </Tabs>
        </Box>

        {/* Tab Productos */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            mb: 2,
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 2, sm: 0 }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                variant="h6"
                sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
              >
                Productos
              </Typography>
              <Chip label={filteredProducts.length} size="small" color="primary" variant="outlined" />
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setProductDialog({ open: true, mode: 'create', product: null })}
              size="small"
              sx={{
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                alignSelf: { xs: 'flex-start', sm: 'auto' }
              }}
            >
              Nuevo Producto
            </Button>
          </Box>

          <TextField
            placeholder="Buscar por nombre o descripción..."
            value={productSearch}
            onChange={(e) => { setProductSearch(e.target.value); setProductPage(0); }}
            size="small"
            fullWidth
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Chip
              label="Todos"
              variant={productStockFilter === 'all' ? 'filled' : 'outlined'}
              onClick={() => { setProductStockFilter('all'); setProductPage(0); }}
              sx={{ fontWeight: productStockFilter === 'all' ? 700 : 400 }}
            />
            <Chip
              label="En Stock"
              variant={productStockFilter === 'stock' ? 'filled' : 'outlined'}
              color="success"
              onClick={() => { setProductStockFilter('stock'); setProductPage(0); }}
              sx={{ fontWeight: productStockFilter === 'stock' ? 700 : 400 }}
            />
            <Chip
              label="Vendidos"
              variant={productStockFilter === 'sold' ? 'filled' : 'outlined'}
              color="primary"
              onClick={() => { setProductStockFilter('sold'); setProductPage(0); }}
              sx={{ fontWeight: productStockFilter === 'sold' ? 700 : 400 }}
            />
          </Stack>

          <TableContainer sx={{ maxHeight: { xs: 400, sm: 'none' } }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    Nombre
                  </TableCell>
                  <TableCell sx={{
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    display: { xs: 'none', sm: 'table-cell' }
                  }}>
                    Descripción
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    Precio de Costo
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, display: { xs: 'none', sm: 'table-cell' } }}>
                    Comprado por
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    Estado
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    Acciones
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredProducts
                  .slice(productPage * rowsPerPage, productPage * rowsPerPage + rowsPerPage)
                  .map((product) => (
                  <TableRow key={product.id}>
                    <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {product.name}
                    </TableCell>
                    <TableCell sx={{
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      display: { xs: 'none', sm: 'table-cell' }
                    }}>
                      {product.description}
                    </TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      ${product.cost_price.toLocaleString()}
                    </TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, display: { xs: 'none', sm: 'table-cell' } }}>
                      {product.buyer_name || '\u2014'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={product.is_sold ? 'Vendido' : 'En Stock'}
                        size="small"
                        color={product.is_sold ? 'primary' : 'success'}
                        sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleEditProduct(product)}
                        color="primary"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteProduct(product)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredProducts.length}
            page={Math.min(productPage, Math.max(0, Math.ceil(filteredProducts.length / rowsPerPage) - 1))}
            onPageChange={(_, p) => setProductPage(p)}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[rowsPerPage]}
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />
        </TabPanel>

        {/* Tab Clientes */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            mb: 2,
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 2, sm: 0 }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                variant="h6"
                sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
              >
                Clientes
              </Typography>
              <Chip label={filteredCustomers.length} size="small" color="primary" variant="outlined" />
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCustomerDialog({ open: true, mode: 'create', customer: null })}
              size="small"
              sx={{ 
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                alignSelf: { xs: 'flex-start', sm: 'auto' }
              }}
            >
              Nuevo Cliente
            </Button>
          </Box>

          <Box sx={{ mb: 2, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5, alignItems: { sm: 'center' } }}>
            <ToggleButtonGroup
              value={customerStatusFilter}
              exclusive
              size="small"
              onChange={(_, v) => {
                if (v) { setCustomerStatusFilter(v); setCustomerPage(0); }
              }}
            >
              <ToggleButton value="active">
                Activos ({customers.filter(c => c.has_active_sale).length})
              </ToggleButton>
              <ToggleButton value="inactive">
                Inactivos ({customers.filter(c => !c.has_active_sale).length})
              </ToggleButton>
              <ToggleButton value="all">
                Todos ({customers.length})
              </ToggleButton>
            </ToggleButtonGroup>
            <TextField
              placeholder="Buscar por apellido, nombre, DNI o teléfono..."
              value={customerSearch}
              onChange={(e) => { setCustomerSearch(e.target.value); setCustomerPage(0); }}
              size="small"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <TableContainer sx={{ maxHeight: { xs: 400, sm: 'none' } }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    Nombre
                  </TableCell>
                  <TableCell sx={{
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    display: { xs: 'none', sm: 'table-cell' }
                  }}>
                    Dirección
                  </TableCell>
                  <TableCell sx={{
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    display: { xs: 'none', md: 'table-cell' }
                  }}>
                    Teléfono
                  </TableCell>
                  <TableCell sx={{
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    display: { xs: 'none', md: 'table-cell' }
                  }}>
                    DNI
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    Acciones
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredCustomers
                  .slice(customerPage * rowsPerPage, customerPage * rowsPerPage + rowsPerPage)
                  .map((customer) => (
                  <TableRow key={customer.id}>
                    <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                        <span>{customer.last_name} {customer.first_name}</span>
                        {customer.has_active_sale ? (
                          <Chip label="Activo" size="small" color="success" variant="outlined" sx={{ height: 20 }} />
                        ) : (
                          <Chip label="Inactivo" size="small" color="default" variant="outlined" sx={{ height: 20 }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      display: { xs: 'none', sm: 'table-cell' }
                    }}>
                      {customer.address}
                    </TableCell>
                    <TableCell sx={{
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      display: { xs: 'none', md: 'table-cell' }
                    }}>
                      {customer.phone}
                    </TableCell>
                    <TableCell sx={{
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      display: { xs: 'none', md: 'table-cell' }
                    }}>
                      {customer.dni}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleEditCustomer(customer)}
                        color="primary"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteCustomer(customer)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredCustomers.length}
            page={Math.min(customerPage, Math.max(0, Math.ceil(filteredCustomers.length / rowsPerPage) - 1))}
            onPageChange={(_, p) => setCustomerPage(p)}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[rowsPerPage]}
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />
        </TabPanel>

        {/* Tab Ventas */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            mb: 2,
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 2, sm: 0 }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                variant="h6"
                sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
              >
                Ventas
              </Typography>
              <Chip label={filteredSales.length} size="small" color="primary" variant="outlined" />
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setSaleItems([]);
                resetProductSelection();
                setPayFirstInstallment(false);
                setFirstInstallmentAlreadyPaid(false);
                setFirstInstallmentMethod('cash');
                setSaleDialog({ open: true, mode: 'create', sale: null });
              }}
              size="small"
              sx={{ 
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                alignSelf: { xs: 'flex-start', sm: 'auto' }
              }}
            >
              Nueva Venta
            </Button>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
            <ToggleButtonGroup
              value={saleRangeMode}
              exclusive
              size="small"
              onChange={(_, newMode) => {
                if (newMode) { setSaleRangeMode(newMode); setSalePage(0); }
              }}
            >
              <ToggleButton value="day">Día</ToggleButton>
              <ToggleButton value="week">Semana</ToggleButton>
              <ToggleButton value="month">Mes</ToggleButton>
            </ToggleButtonGroup>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
              <DatePicker
                label={
                  saleRangeMode === 'day'
                    ? 'Seleccionar día'
                    : saleRangeMode === 'week'
                      ? 'Cualquier día de la semana'
                      : 'Seleccionar mes'
                }
                value={saleDateFilter}
                onChange={(d) => { setSaleDateFilter(d); setSalePage(0); }}
                views={saleRangeMode === 'month' ? ['year', 'month'] : ['year', 'month', 'day']}
                openTo={saleRangeMode === 'month' ? 'month' : 'day'}
                slots={{ textField: TextField }}
              />
            </LocalizationProvider>
            {saleDateFilter && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => { setSaleDateFilter(null); setSalePage(0); }}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Ver todas
              </Button>
            )}
          </Box>
          {saleDateFilter && saleRangeMode !== 'day' && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              {saleRangeMode === 'week'
                ? `Semana del ${format(startOfWeek(saleDateFilter, { weekStartsOn: 1 }), 'dd/MM')} al ${format(endOfWeek(saleDateFilter, { weekStartsOn: 1 }), 'dd/MM')}`
                : format(saleDateFilter, 'MMMM yyyy', { locale: es })}
            </Typography>
          )}

          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <TextField
              placeholder="Buscar por cliente, producto, vendedor o cobrador..."
              value={saleSearch}
              onChange={(e) => { setSaleSearch(e.target.value); setSalePage(0); }}
              size="small"
              sx={{ flex: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Cobrador</InputLabel>
              <Select
                value={saleCollectorFilter}
                onChange={(e) => { setSaleCollectorFilter(e.target.value); setSalePage(0); }}
                label="Cobrador"
              >
                <MenuItem value="">Todos</MenuItem>
                {appUsers.map(u => (
                  <MenuItem key={u.id} value={u.username}>{u.username}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <TableContainer sx={{ maxHeight: { xs: 400, sm: 'none' } }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    Venta
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    Fecha de carga
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    Cliente
                  </TableCell>
                  <TableCell sx={{
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    display: { xs: 'none', sm: 'table-cell' }
                  }}>
                    Producto
                  </TableCell>
                  <TableCell sx={{
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    display: { xs: 'none', md: 'table-cell' }
                  }}>
                    Monto Total
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    Cuotas
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    Debe
                  </TableCell>
                  <TableCell sx={{
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    display: { xs: 'none', sm: 'table-cell' }
                  }}>
                    Vendedor
                  </TableCell>
                  <TableCell sx={{
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    display: { xs: 'none', sm: 'table-cell' }
                  }}>
                    Cobrador
                  </TableCell>
                  <TableCell sx={{
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    display: { xs: 'none', md: 'table-cell' }
                  }}>
                    Estado
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    Acciones
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredSales
                  .slice(salePage * rowsPerPage, salePage * rowsPerPage + rowsPerPage)
                  .map((sale) => (
                  <TableRow key={sale.id}>
                    <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      #{sale.id}
                    </TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}>
                      {sale.created_at ? new Date(sale.created_at).toLocaleDateString('es-AR') : '-'}
                    </TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {sale.customer.last_name} {sale.customer.first_name}
                    </TableCell>
                    <TableCell sx={{
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      display: { xs: 'none', sm: 'table-cell' }
                    }}>
                      {sale.notes && sale.notes.startsWith('Productos: ')
                        ? sale.notes.replace('Productos: ', '')
                        : sale.product.name}
                    </TableCell>
                    <TableCell sx={{
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      display: { xs: 'none', md: 'table-cell' }
                    }}>
                      ${sale.total_amount.toLocaleString()}
                    </TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {sale.installments}
                    </TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {!sale.is_active
                        ? <Chip label="Completada" size="small" color="success" sx={{ fontSize: '0.7rem' }} />
                        : <SaleOverdueChip sale={sale} />
                      }
                    </TableCell>
                    <TableCell sx={{
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      display: { xs: 'none', sm: 'table-cell' }
                    }}>
                      {sale.vendor_name ? (
                        <Chip label={sale.vendor_name} size="small" variant="outlined" />
                      ) : '-'}
                    </TableCell>
                    <TableCell sx={{
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      display: { xs: 'none', sm: 'table-cell' }
                    }}>
                      {sale.collector_name ? (
                        <Chip label={sale.collector_name} size="small" variant="outlined" />
                      ) : '-'}
                    </TableCell>
                    <TableCell sx={{
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      display: { xs: 'none', md: 'table-cell' }
                    }}>
                      {sale.is_active ? 'Activa' : 'Inactiva'}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleEditSale(sale)}
                        color="primary"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteSale(sale)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredSales.length}
            page={Math.min(salePage, Math.max(0, Math.ceil(filteredSales.length / rowsPerPage) - 1))}
            onPageChange={(_, p) => setSalePage(p)}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[rowsPerPage]}
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />
        </TabPanel>

        {/* Tab Gastos */}
        <TabPanel value={tabValue} index={3}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Vista</InputLabel>
                  <Select
                    value={expenseViewMode}
                    label="Vista"
                    onChange={(e) => setExpenseViewMode(e.target.value as 'daily' | 'weekly')}
                  >
                    <MenuItem value="daily">Diaria</MenuItem>
                    <MenuItem value="weekly">Semanal</MenuItem>
                  </Select>
                </FormControl>
                {expenseViewMode === 'daily' ? (
                  <DatePicker
                    label="Seleccionar día"
                    value={selectedExpenseDate}
                    onChange={(d) => d && setSelectedExpenseDate(d)}
                    slots={{ textField: TextField }}
                  />
                ) : (
                  <DatePicker
                    label="Seleccionar semana (miércoles)"
                    value={selectedExpenseWeek}
                    onChange={(d) => {
                      if (!d) return;
                      const date = new Date(d);
                      date.setHours(0, 0, 0, 0);
                      const jsDay = date.getDay();
                      const offsetToWednesday = (jsDay + 4) % 7;
                      date.setDate(date.getDate() - offsetToWednesday);
                      setSelectedExpenseWeek(date);
                    }}
                    slots={{ textField: TextField }}
                  />
                )}
              </Box>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => {
                setExpenseForm({ description: '', total: '', cash_amount: '', transfer_amount: '', category: '', responsible: '', is_product: false, expense_date: selectedExpenseDate || new Date() });
                setExpenseDialog({ open: true, mode: 'create', expense: null });
              }}>Nuevo gasto</Button>
            </Box>
          </LocalizationProvider>

          <Dialog open={expenseDialog.open} onClose={() => setExpenseDialog({ open: false, mode: 'create', expense: null })} maxWidth="sm" fullWidth>
            <DialogTitle>{expenseDialog.mode === 'create' ? 'Nuevo Gasto' : 'Editar Gasto'}</DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                    <DatePicker
                      label="Fecha del gasto"
                      value={expenseForm.expense_date}
                      onChange={(d) => d && setExpenseForm({ ...expenseForm, expense_date: d })}
                      slots={{ textField: TextField }}
                      slotProps={{ textField: { size: 'small', fullWidth: true } }}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Descripción"
                    fullWidth
                    size="small"
                    value={expenseForm.description}
                    onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
                  />
                </Grid>
                {/* Sin campo de Monto total - mostrar total calculado */}
                <Grid item xs={6} sm={3}>
                  <TextField
                    label="Efectivo"
                    type="number"
                    fullWidth
                    size="small"
                    value={expenseForm.cash_amount}
                    onChange={(e) => {
                      setExpenseForm({ ...expenseForm, cash_amount: e.target.value });
                    }}
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <TextField
                    label="Transferencia"
                    type="number"
                    fullWidth
                    size="small"
                    value={expenseForm.transfer_amount}
                    onChange={(e) => {
                      setExpenseForm({ ...expenseForm, transfer_amount: e.target.value });
                    }}
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Alert severity="info">
                    Total: ${
                      (
                        (parseFloat((expenseForm.cash_amount || '0') as any) || 0) +
                        (parseFloat((expenseForm.transfer_amount || '0') as any) || 0)
                      ).toLocaleString()
                    }
                  </Alert>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Categoría"
                    fullWidth
                    size="small"
                    value={expenseForm.category}
                    onChange={(e) => setExpenseForm({ ...expenseForm, category: e.target.value })}
                    placeholder="general"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small" required>
                    <InputLabel>Responsable</InputLabel>
                    <Select
                      value={expenseForm.responsible}
                      label="Responsable"
                      onChange={(e) => setExpenseForm({ ...expenseForm, responsible: e.target.value as string })}
                    >
                      <MenuItem value="Daniel">Daniel</MenuItem>
                      <MenuItem value="Juan">Juan</MenuItem>
                      <MenuItem value="Gonzalo">Gonzalo</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={expenseForm.is_product}
                        onChange={(e) => setExpenseForm({ ...expenseForm, is_product: e.target.checked })}
                      />
                    }
                    label="Es un producto"
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setExpenseDialog({ open: false, mode: 'create', expense: null })}>Cancelar</Button>
              <Button variant="contained" onClick={expenseDialog.mode === 'create' ? handleCreateExpense : handleUpdateExpense}>Guardar</Button>
            </DialogActions>
          </Dialog>

          {expenseViewMode === 'daily' ? (
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>Gastos del día</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Hora</TableCell>
                    <TableCell>Descripción</TableCell>
                    <TableCell>Categoría</TableCell>
                    <TableCell>Responsable</TableCell>
                    <TableCell align="right">Efectivo</TableCell>
                    <TableCell align="right">Transferencia</TableCell>
                    <TableCell align="right">Monto total</TableCell>
                    <TableCell align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {expenses.length === 0 ? (
                    <TableRow><TableCell colSpan={8}>Sin gastos para el día</TableCell></TableRow>
                  ) : expenses.map((exp: any, idx: number) => (
                    <TableRow key={idx}>
                      <TableCell>{exp.time}</TableCell>
                      <TableCell>{exp.description}</TableCell>
                      <TableCell>{exp.category || '-'}</TableCell>
                      <TableCell>{exp.responsible || (() => { try { return JSON.parse(exp.notes || '{}').responsible || '-'; } catch { return '-'; } })()}</TableCell>
                      <TableCell align="right">${Number(exp.cash || 0).toLocaleString()}</TableCell>
                      <TableCell align="right">${Number(exp.transfer || 0).toLocaleString()}</TableCell>
                      <TableCell align="right">${Number(exp.expense || 0).toLocaleString()}</TableCell>
                      <TableCell align="center">
                        {exp.expense_id ? (
                          <>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => {
                                let parsedResponsible = '';
                                try { parsedResponsible = JSON.parse(exp.notes || '{}').responsible || ''; } catch {}
                                setExpenseForm({
                                  description: exp.description || '',
                                  total: '',
                                  cash_amount: String(exp.cash || 0),
                                  transfer_amount: String(exp.transfer || 0),
                                  category: exp.category || '',
                                  responsible: parsedResponsible,
                                  is_product: false,
                                  expense_date: selectedExpenseDate || new Date()
                                });
                                setExpenseDialog({ open: true, mode: 'edit', expense: exp });
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteExpense(exp)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                  {expenses.length > 0 && (
                    <TableRow sx={{ backgroundColor: 'action.hover' }}>
                      <TableCell sx={{ fontWeight: 700 }} colSpan={4}>TOTAL</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        ${expenses.reduce((s: number, e: any) => s + Number(e.cash || 0), 0).toLocaleString()}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        ${expenses.reduce((s: number, e: any) => s + Number(e.transfer || 0), 0).toLocaleString()}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: 'error.main' }}>
                        ${expenses.reduce((s: number, e: any) => s + Number(e.expense || 0), 0).toLocaleString()}
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Paper>
          ) : (
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Gastos de la semana ({(() => {
                  const start = new Date(selectedExpenseWeek);
                  const end = new Date(start);
                  end.setDate(start.getDate() + 6);
                  return `${start.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' })} - ${end.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' })}`;
                })()})
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Día</TableCell>
                    <TableCell align="right">Ingresos</TableCell>
                    <TableCell align="right">Gastos</TableCell>
                    <TableCell align="right">Efectivo</TableCell>
                    <TableCell align="right">Transferencia</TableCell>
                    <TableCell align="right">Neto</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {weeklyExpenses.length === 0 ? (
                    <TableRow><TableCell colSpan={6}>Sin datos para la semana</TableCell></TableRow>
                  ) : weeklyExpenses.map((day: any, idx: number) => {
                    const dayDate = new Date(day.day + 'T12:00:00');
                    const dayName = dayDate.toLocaleDateString('es-AR', { weekday: 'short', day: '2-digit', month: '2-digit' });
                    const net = (day.income || 0) - (day.expense || 0);
                    return (
                      <TableRow key={idx}>
                        <TableCell sx={{ textTransform: 'capitalize' }}>{dayName}</TableCell>
                        <TableCell align="right" sx={{ color: 'success.main' }}>${Number(day.income || 0).toLocaleString()}</TableCell>
                        <TableCell align="right" sx={{ color: 'error.main' }}>${Number(day.expense || 0).toLocaleString()}</TableCell>
                        <TableCell align="right">${Number(day.cash || 0).toLocaleString()}</TableCell>
                        <TableCell align="right">${Number(day.transfer || 0).toLocaleString()}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, color: net >= 0 ? 'success.main' : 'error.main' }}>${net.toLocaleString()}</TableCell>
                      </TableRow>
                    );
                  })}
                  {weeklyExpenses.length > 0 && (
                    <TableRow sx={{ backgroundColor: 'action.hover' }}>
                      <TableCell sx={{ fontWeight: 700 }}>TOTAL</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: 'success.main' }}>
                        ${weeklyExpenses.reduce((s: number, d: any) => s + (d.income || 0), 0).toLocaleString()}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: 'error.main' }}>
                        ${weeklyExpenses.reduce((s: number, d: any) => s + (d.expense || 0), 0).toLocaleString()}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        ${weeklyExpenses.reduce((s: number, d: any) => s + (d.cash || 0), 0).toLocaleString()}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        ${weeklyExpenses.reduce((s: number, d: any) => s + (d.transfer || 0), 0).toLocaleString()}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        ${(() => {
                          const total = weeklyExpenses.reduce((s: number, d: any) => s + (d.income || 0) - (d.expense || 0), 0);
                          return total.toLocaleString();
                        })()}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              {weeklyExpensesByResponsible.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Gastos por responsable</Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Responsable</TableCell>
                        <TableCell align="right">Cant.</TableCell>
                        <TableCell align="right">Efectivo</TableCell>
                        <TableCell align="right">Transferencia</TableCell>
                        <TableCell align="right">Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {weeklyExpensesByResponsible.map((r: any, idx: number) => (
                        <TableRow key={idx}>
                          <TableCell sx={{ textTransform: 'capitalize' }}>{r.name}</TableCell>
                          <TableCell align="right">{r.count}</TableCell>
                          <TableCell align="right">${Number(r.cash || 0).toLocaleString()}</TableCell>
                          <TableCell align="right">${Number(r.transfer || 0).toLocaleString()}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, color: 'error.main' }}>
                            ${Number(r.total || 0).toLocaleString()}
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ backgroundColor: 'action.hover' }}>
                        <TableCell sx={{ fontWeight: 700 }}>TOTAL</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          {weeklyExpensesByResponsible.reduce((s: number, r: any) => s + (r.count || 0), 0)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          ${weeklyExpensesByResponsible.reduce((s: number, r: any) => s + (r.cash || 0), 0).toLocaleString()}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          ${weeklyExpensesByResponsible.reduce((s: number, r: any) => s + (r.transfer || 0), 0).toLocaleString()}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: 'error.main' }}>
                          ${weeklyExpensesByResponsible.reduce((s: number, r: any) => s + (r.total || 0), 0).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Box>
              )}
            </Paper>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={4}>
          <Typography variant="h6" sx={{ mb: 2 }}>Gestión de Usuarios</Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Usuario</TableCell>
                  <TableCell>Rol</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {appUsers.map((u) => (
                  <TableRow key={u.id}>
                    <TableCell>{u.username}</TableCell>
                    <TableCell>
                      <Chip
                        label={u.role}
                        size="small"
                        color={u.role === 'admin' ? 'primary' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={u.is_active ? 'Activo' : 'Inactivo'}
                        size="small"
                        color={u.is_active ? 'success' : 'error'}
                      />
                    </TableCell>
                    <TableCell>
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <Select
                          value={u.role}
                          onChange={async (e) => {
                            try {
                              await api.put(`/users/${u.id}/role`, { role: e.target.value });
                              setAppUsers(prev => prev.map(user =>
                                user.id === u.id ? { ...user, role: e.target.value as string } : user
                              ));
                            } catch (err) {
                              console.error('Error updating role:', err);
                            }
                          }}
                        >
                          <MenuItem value="admin">Admin</MenuItem>
                          <MenuItem value="cobrador">Cobrador</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

      </Paper>

      {/* Diálogo Producto */}
      <Dialog 
        open={productDialog.open} 
        onClose={() => setProductDialog({ open: false, mode: 'create', product: null })} 
        maxWidth="sm" 
        fullWidth
        fullScreen={window.innerWidth < 600}
      >
        <DialogTitle sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
          {productDialog.mode === 'create' ? 'Nuevo Producto' : 'Editar Producto'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nombre"
                value={productForm.name}
                onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descripción"
                multiline
                rows={3}
                value={productForm.description}
                onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Precio de Costo"
                type="number"
                value={productForm.cost_price}
                onChange={(e) => setProductForm({ ...productForm, cost_price: parseFloat(e.target.value) })}
                size="small"
              />
            </Grid>
            {productDialog.mode === 'create' && (
              <Grid item xs={12} sm={6}>
                <Autocomplete<{ id: number; name: string }>
                  options={vendors}
                  getOptionLabel={(option) => option.name}
                  value={vendors.find(v => v.name === productForm.buyer_name) || null}
                  onChange={(_, newValue) => setProductForm({ ...productForm, buyer_name: newValue?.name || '' })}
                  renderInput={(params) => (
                    <TextField {...params} label="Comprado por" size="small" placeholder="¿Quién compró?" />
                  )}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
          <Button 
            onClick={() => setProductDialog({ open: false, mode: 'create', product: null })}
            size="small"
          >
            Cancelar
          </Button>
          <Button 
            onClick={productDialog.mode === 'create' ? handleCreateProduct : handleUpdateProduct} 
            variant="contained"
            size="small"
          >
            {productDialog.mode === 'create' ? 'Crear' : 'Actualizar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo Cliente */}
      <Dialog 
        open={customerDialog.open} 
        onClose={() => setCustomerDialog({ open: false, mode: 'create', customer: null })} 
        maxWidth="sm" 
        fullWidth
        fullScreen={window.innerWidth < 600}
      >
        <DialogTitle sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
          {customerDialog.mode === 'create' ? 'Nuevo Cliente' : 'Editar Cliente'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nombre"
                value={customerForm.first_name}
                onChange={(e) => setCustomerForm({ ...customerForm, first_name: e.target.value })}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Apellido"
                value={customerForm.last_name}
                onChange={(e) => setCustomerForm({ ...customerForm, last_name: e.target.value })}
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Dirección"
                value={customerForm.address}
                onChange={(e) => setCustomerForm({ ...customerForm, address: e.target.value })}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Teléfono"
                value={customerForm.phone}
                onChange={(e) => setCustomerForm({ ...customerForm, phone: e.target.value })}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="DNI"
                value={customerForm.dni}
                onChange={(e) => setCustomerForm({ ...customerForm, dni: e.target.value })}
                size="small"
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
          <Button 
            onClick={() => setCustomerDialog({ open: false, mode: 'create', customer: null })}
            size="small"
          >
            Cancelar
          </Button>
          <Button 
            onClick={customerDialog.mode === 'create' ? handleCreateCustomer : handleUpdateCustomer} 
            variant="contained"
            size="small"
            disabled={!customerForm.dni.trim()}
          >
            {customerDialog.mode === 'create' ? 'Crear' : 'Actualizar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo Venta */}
      <Dialog
        open={saleDialog.open}
        onClose={() => {
          setSaleDialog({ open: false, mode: 'create', sale: null });
          setSaleItems([]);
          resetProductSelection();
        }}
        maxWidth="md"
        fullWidth
        fullScreen={window.innerWidth < 600}
      >
        <DialogTitle sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 600 }}>
          {saleDialog.mode === 'create' ? 'Nueva Venta' : 'Editar Venta'}
        </DialogTitle>
    <DialogContent
      dividers
      sx={(theme) => ({
        backgroundColor:
          theme.palette.mode === 'dark'
            ? theme.palette.background.paper
            : 'grey.50',
        p: { xs: 2, sm: 3 }
      })}
    >
          <Stack spacing={3}>
            <Paper
              variant="outlined"
              sx={(theme) => ({
                p: { xs: 2, sm: 3 },
                boxShadow: 'none',
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? theme.palette.background.default
                    : theme.palette.background.paper,
                borderColor:
                  theme.palette.mode === 'dark'
                    ? 'rgba(148, 163, 184, 0.25)'
                    : theme.palette.divider,
              })}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                1. Selecciona el cliente
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Busca por apellido, nombre o teléfono para asociar la venta.
              </Typography>
              <Autocomplete<Customer>
                options={customers}
                getOptionLabel={(option) => `${option.last_name} ${option.first_name}${option.phone ? ` · ${option.phone}` : ''}${option.dni ? ` · DNI ${option.dni}` : ''}`}
                value={selectedCustomer}
                onChange={(_, newValue) => setSaleForm({ ...saleForm, customer_id: newValue?.id ?? 0 })}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                noOptionsText="Sin resultados"
                renderInput={(params) => (
                  <TextField {...params} label="Cliente" placeholder="Comienza a escribir para buscar" size="small" />
                )}
              />
              {selectedCustomer && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} mt={2} flexWrap="wrap">
                  <Chip icon={<PersonIcon fontSize="small" />} label={`${selectedCustomer.last_name} ${selectedCustomer.first_name}`} />
                  {selectedCustomer.phone && <Chip icon={<PhoneIcon fontSize="small" />} label={selectedCustomer.phone} />}
                  {selectedCustomer.dni && <Chip icon={<BadgeIcon fontSize="small" />} label={`DNI ${selectedCustomer.dni}`} />}
                  {selectedCustomer.address && <Chip icon={<PlaceIcon fontSize="small" />} label={selectedCustomer.address} />}
                </Stack>
              )}
            </Paper>

            <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, boxShadow: 'none', backgroundColor: 'background.paper' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                2. Producto a financiar
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Elige el producto o servicio que corresponde a la venta.
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={6}>
                  <Autocomplete<Product>
                    options={products.filter(p =>
                      !p.is_sold && !saleItems.some(item => item.product.id === p.id)
                    )}
                    getOptionLabel={(option) => {
                      return `${option.name} · ${formatCurrency(option.cost_price)}`;
                    }}
                    value={productSelection.product}
                    onChange={(_, newValue) =>
                      setProductSelection((prev) => ({ ...prev, product: newValue || null }))
                    }
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    noOptionsText="Sin resultados"
                    renderInput={(params) => (
                      <TextField {...params} label="Producto" placeholder="Busca por nombre" size="small" />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  <TextField
                    label="Cantidad"
                    type="number"
                    size="small"
                    value={productSelection.quantity}
                    onChange={(event) => {
                      const raw = Number(event.target.value);
                      const normalized = Number.isFinite(raw) && raw > 0 ? Math.floor(raw) : 1;
                      setProductSelection((prev) => ({ ...prev, quantity: Math.max(1, normalized) }));
                    }}
                    inputProps={{ min: 1 }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={8} md={3}>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    fullWidth
                    onClick={handleAddSaleItem}
                  >
                    Agregar a la venta
                  </Button>
                </Grid>
              </Grid>

              {saleItems.length > 0 && (
                <Stack spacing={1.5} mt={2}>
                  {saleItems.map((item) => {
                    const subtotal = (item.product.cost_price || 0) * item.quantity;
                    return (
                      <Box
                        key={item.product.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          p: 1.5,
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          bgcolor: 'background.default',
                          flexWrap: 'wrap',
                        }}
                      >
                        <Box sx={{ flexGrow: 1, minWidth: 200 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {item.product.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {`${formatCurrency(item.product.cost_price)} c/u · Subtotal ${formatCurrency(subtotal)}`}
                          </Typography>
                        </Box>
                        <TextField
                          label="Cantidad"
                          type="number"
                          size="small"
                          value={item.quantity}
                          onChange={(event) => handleSaleItemQuantityChange(item.product.id, Number(event.target.value))}
                          inputProps={{ min: 1 }}
                          sx={{ width: 120 }}
                        />
                        <IconButton color="error" onClick={() => handleRemoveSaleItem(item.product.id)} size="small">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </Paper>

            <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, boxShadow: 'none', backgroundColor: 'background.paper' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                3. Vendedor
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Selecciona un vendedor o agrega uno nuevo.
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    freeSolo
                    options={vendors.map(v => v.name)}
                    value={saleForm.vendor_name || ''}
                    onChange={(_, newValue) => setSaleForm({ ...saleForm, vendor_name: (newValue as string) || '' })}
                    onInputChange={(_, newInputValue) => setSaleForm({ ...saleForm, vendor_name: newInputValue || '' })}
                    renderInput={(params) => (
                      <TextField {...params} label="Seleccionar o escribir vendedor" size="small" placeholder="Vendedor" />
                    )}
                  />
                </Grid>
                {/* Opción de alta inline removida a pedido */}
              </Grid>
            </Paper>

            <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, boxShadow: 'none', backgroundColor: 'background.paper' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                4. Cobrador asignado
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Selecciona quién va a cobrar esta venta.
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6}>
                  <Autocomplete<AppUser>
                    options={appUsers.filter(u => u.is_active)}
                    getOptionLabel={(option) => {
                      const name = option.username.includes('@') ? option.username.split('@')[0] : option.username;
                      return `${name.charAt(0).toUpperCase() + name.slice(1)} (${option.role})`;
                    }}
                    value={appUsers.find(u => {
                      const name = u.username.includes('@') ? u.username.split('@')[0] : u.username;
                      const displayName = name.charAt(0).toUpperCase() + name.slice(1);
                      return displayName === saleForm.collector_name || u.username === saleForm.collector_name;
                    }) || null}
                    onChange={(_, newValue) => {
                      const name = newValue?.username.includes('@') ? newValue.username.split('@')[0] : newValue?.username || '';
                      setSaleForm({ ...saleForm, collector_name: name ? name.charAt(0).toUpperCase() + name.slice(1) : '' });
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Seleccionar cobrador" size="small" placeholder="Cobrador" />
                    )}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                  />
                </Grid>
              </Grid>
            </Paper>

            <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, boxShadow: 'none', backgroundColor: 'background.paper' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                5. Condiciones de la venta
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Ingresa cuotas y monto por cuota. El total se calcula automaticamente.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Precio de Costo (Base)"
                    type="number"
                    value={saleForm.cost_price || resolveCostPrice()}
                    onChange={(e) => handleCostPriceChange(parseFloat(e.target.value))}
                    size="small"
                    inputProps={{ min: 0, step: 0.01 }}
                    helperText="Lo que te costo el producto"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Numero de Cuotas"
                    type="number"
                    value={saleForm.installments}
                    onChange={(e) => handleInstallmentsChange(parseInt(e.target.value, 10))}
                    size="small"
                    inputProps={{ min: 1 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Monto por Cuota"
                    type="number"
                    value={saleForm.installment_amount}
                    onChange={(e) => handleInstallmentAmountChange(parseFloat(e.target.value))}
                    size="small"
                    inputProps={{ min: 0, step: 0.01 }}
                    helperText="Valor de cada cuota"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Monto Total (calculado)"
                    type="number"
                    value={saleForm.total_amount}
                    InputProps={{ readOnly: true }}
                    size="small"
                    helperText="Cuotas x Monto por cuota"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    select
                    label="Día de Cobro Semanal (opcional)"
                    value={saleForm.collection_day}
                    onChange={(e) => setSaleForm({ ...saleForm, collection_day: parseInt(e.target.value, 10) })}
                    size="small"
                    SelectProps={{ native: true }}
                  >
                    <option value={0}>Sin día fijo</option>
                    {COLLECTION_DAY_LABELS.slice(1).map((label, index) => (
                      <option key={label} value={index + 1}>{label}</option>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                Calculamos automáticamente las fechas de inicio y fin estimadas según el día de cobro semanal y la cantidad de cuotas.
              </Typography>
              <Grid container spacing={1.5} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={4}>
                  <Box
                    sx={(theme) => ({
                      p: 1.5,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor:
                        theme.palette.mode === 'dark'
                          ? 'rgba(148, 163, 184, 0.25)'
                          : theme.palette.divider,
                      bgcolor:
                        theme.palette.mode === 'dark'
                          ? 'rgba(30, 41, 59, 0.75)'
                          : theme.palette.background.default,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      backdropFilter: theme.palette.mode === 'dark' ? 'blur(6px)' : 'none',
                    })}
                  >
                    <CalendarTodayIcon fontSize="small" color="primary" />
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Inicio estimado
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {estimatedStartLabel}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box
                    sx={(theme) => ({
                      p: 1.5,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor:
                        theme.palette.mode === 'dark'
                          ? 'rgba(148, 163, 184, 0.25)'
                          : theme.palette.divider,
                      bgcolor:
                        theme.palette.mode === 'dark'
                          ? 'rgba(30, 41, 59, 0.75)'
                          : theme.palette.background.default,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      backdropFilter: theme.palette.mode === 'dark' ? 'blur(6px)' : 'none',
                    })}
                  >
                    <CalendarTodayIcon fontSize="small" color="primary" />
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Fin estimado
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {estimatedEndLabel}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box
                    sx={(theme) => ({
                      p: 1.5,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor:
                        theme.palette.mode === 'dark'
                          ? 'rgba(148, 163, 184, 0.25)'
                          : theme.palette.divider,
                      bgcolor:
                        theme.palette.mode === 'dark'
                          ? 'rgba(30, 41, 59, 0.75)'
                          : theme.palette.background.default,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      backdropFilter: theme.palette.mode === 'dark' ? 'blur(6px)' : 'none',
                    })}
                  >
                    <PaymentsIcon fontSize="small" color="secondary" />
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Día de cobro
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {collectionDayLabel}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
              {!estimatedSchedule.start && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Completa la cantidad de cuotas para obtener una estimación precisa.
                </Typography>
              )}
            </Paper>

            <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, boxShadow: 'none', backgroundColor: 'background.paper' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                6. Primera cuota
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={payFirstInstallment}
                    disabled={firstInstallmentAlreadyPaid}
                    onChange={(e) => setPayFirstInstallment(e.target.checked)}
                  />
                }
                label={
                  firstInstallmentAlreadyPaid
                    ? 'La primera cuota ya figura pagada'
                    : saleDialog.mode === 'edit'
                      ? 'Registrar el pago de la primera cuota'
                      : 'El cliente paga la primera cuota hoy'
                }
              />
              {saleDialog.mode === 'edit' && payFirstInstallment && !firstInstallmentAlreadyPaid && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Se registrará la cobranza con fecha de hoy y se reflejará en Cobranzas.
                </Typography>
              )}
              {payFirstInstallment && !firstInstallmentAlreadyPaid && (
                <FormControl size="small" sx={{ ml: 2, minWidth: 150 }}>
                  <InputLabel>Forma de pago</InputLabel>
                  <Select
                    value={firstInstallmentMethod}
                    onChange={(e) => setFirstInstallmentMethod(e.target.value as 'cash' | 'transfer')}
                    label="Forma de pago"
                  >
                    <MenuItem value="cash">Efectivo</MenuItem>
                    <MenuItem value="transfer">Transferencia</MenuItem>
                  </Select>
                </FormControl>
              )}
            </Paper>

            <Paper
              variant="outlined"
              sx={(theme) => ({
                p: { xs: 2, sm: 3 },
                boxShadow: 'none',
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? theme.palette.background.default
                    : theme.palette.background.paper,
                borderColor:
                  theme.palette.mode === 'dark'
                    ? 'rgba(148, 163, 184, 0.25)'
                    : theme.palette.divider,
              })}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                7. Resumen para confirmar
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Revisa los valores calculados antes de guardar la venta.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Box
                    sx={(theme) => ({
                      p: 2,
                      borderRadius: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.5,
                      bgcolor:
                        theme.palette.mode === 'dark'
                          ? 'rgba(15, 23, 42, 0.8)'
                          : 'grey.100',
                      border: theme.palette.mode === 'dark'
                        ? '1px solid rgba(148, 163, 184, 0.25)'
                        : '1px solid transparent',
                      backdropFilter: theme.palette.mode === 'dark' ? 'blur(6px)' : 'none',
                    })}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <AttachMoneyIcon fontSize="small" color="success" />
                      <Typography variant="caption" color="text.secondary">Monto total</Typography>
                    </Stack>
                    <Typography variant="h6" color="success.main">{formatCurrency(saleForm.total_amount)}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box
                    sx={(theme) => ({
                      p: 2,
                      borderRadius: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.5,
                      bgcolor:
                        theme.palette.mode === 'dark'
                          ? 'rgba(15, 23, 42, 0.8)'
                          : 'grey.100',
                      border: theme.palette.mode === 'dark'
                        ? '1px solid rgba(148, 163, 184, 0.25)'
                        : '1px solid transparent',
                      backdropFilter: theme.palette.mode === 'dark' ? 'blur(6px)' : 'none',
                    })}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <PaymentsIcon fontSize="small" color="primary" />
                      <Typography variant="caption" color="text.secondary">Monto por cuota</Typography>
                    </Stack>
                    <Typography variant="h6" color="primary.main">{formatCurrency(saleForm.installment_amount)}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box
                    sx={(theme) => ({
                      p: 2,
                      borderRadius: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.5,
                      bgcolor:
                        theme.palette.mode === 'dark'
                          ? 'rgba(15, 23, 42, 0.8)'
                          : 'grey.100',
                      border: theme.palette.mode === 'dark'
                        ? '1px solid rgba(148, 163, 184, 0.25)'
                        : '1px solid transparent',
                      backdropFilter: theme.palette.mode === 'dark' ? 'blur(6px)' : 'none',
                    })}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <AttachMoneyIcon fontSize="small" color="secondary" />
                      <Typography variant="caption" color="text.secondary">Costo base</Typography>
                    </Stack>
                    <Typography variant="h6" color="text.primary">{formatCurrency(resolveCostPrice())}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box
                    sx={(theme) => ({
                      p: 2,
                      borderRadius: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.5,
                      bgcolor:
                        theme.palette.mode === 'dark'
                          ? 'rgba(15, 23, 42, 0.8)'
                          : 'grey.100',
                      border: theme.palette.mode === 'dark'
                        ? '1px solid rgba(148, 163, 184, 0.25)'
                        : '1px solid transparent',
                      backdropFilter: theme.palette.mode === 'dark' ? 'blur(6px)' : 'none',
                    })}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <PercentIcon fontSize="small" color="warning" />
                      <Typography variant="caption" color="text.secondary">Interés aplicado</Typography>
                    </Stack>
                    <Typography variant="h6" color="warning.main">{formattedInterestRate.toLocaleString(undefined, { maximumFractionDigits: 2 })}%</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
          <Button 
            onClick={() => setSaleDialog({ open: false, mode: 'create', sale: null })}
            size="small"
          >
            Cancelar
          </Button>
          <Button 
            onClick={saleDialog.mode === 'create' ? handleCreateSale : handleUpdateSale} 
            variant="contained"
            size="small"
            disabled={!isSaleFormValid}
          >
            {saleDialog.mode === 'create' ? 'Crear' : 'Actualizar'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Diálogo de confirmación de eliminación */}
      <DeleteConfirmDialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog(createInitialDeleteDialogState())}
        onConfirm={confirmDelete}
        title={deleteEntityLabel ? `Eliminar ${deleteEntityLabel}` : 'Eliminar'}
        message={`¿Estás seguro de que deseas eliminar este ${deleteDialogMessageSubject}?`}
        itemName={deleteDialogItemLabel}
        loading={deleteDialog.loading}
        error={deleteDialog.error}
      />
    </Container>
  );
}

function getInstallmentDueDate(startDate: string, collectionDay: number | null, installmentNumber: number): Date {
  const start = new Date(startDate + 'T12:00:00');
  // First due = next collection day after start + 1 day
  const firstDue = new Date(start);
  firstDue.setDate(firstDue.getDate() + 1);
  if (collectionDay && collectionDay >= 1 && collectionDay <= 7) {
    // collectionDay: 1=Lunes ... 7=Domingo → JS: 1=Mon ... 0=Sun
    const targetJs = collectionDay === 7 ? 0 : collectionDay;
    while (firstDue.getDay() !== targetJs) {
      firstDue.setDate(firstDue.getDate() + 1);
    }
  }
  const due = new Date(firstDue);
  due.setDate(due.getDate() + (installmentNumber - 1) * 7);
  return due;
}

function SaleOverdueChip({ sale }: { sale: Sale }) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Waterfall: determine which installments are unpaid
  let remainingCredit = sale.total_paid || 0;
  const overdueDetails: { installment_number: number; amount: number; due_date: Date }[] = [];

  for (let inst = 1; inst <= sale.installments; inst++) {
    const amt = sale.installment_amount;
    if (remainingCredit >= amt - 0.01) {
      remainingCredit -= amt;
      continue;
    }
    const owed = amt - Math.max(remainingCredit, 0);
    remainingCredit = 0;
    const due = getInstallmentDueDate(sale.start_date, sale.collection_day, inst);
    if (due < today) {
      overdueDetails.push({ installment_number: inst, amount: owed, due_date: due });
    }
  }

  const count = overdueDetails.length;
  if (count === 0) return <Chip label="Al día" size="small" color="success" sx={{ fontSize: '0.7rem' }} />;

  const fmt = (n: number) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(n);
  const fmtDate = (d: Date) => `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}`;

  return (
    <>
      <Chip
        label={`Debe ${count}`}
        size="small"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          fontSize: '0.7rem',
          fontWeight: 700,
          backgroundColor: 'rgba(239,68,68,0.15)',
          color: '#EF4444',
          cursor: 'pointer',
          '&:hover': { backgroundColor: 'rgba(239,68,68,0.25)' },
        }}
      />
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, minWidth: 240 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', mb: 1 }}>
            Cuotas adeudadas ({count})
          </Typography>
          {overdueDetails.map((d) => (
            <Box key={d.installment_number} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography sx={{ fontSize: '0.8rem' }}>
                Cuota {d.installment_number} — {fmtDate(d.due_date)}
              </Typography>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#EF4444', ml: 2 }}>
                {fmt(d.amount)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Popover>
    </>
  );
}
